import { useTranslation } from "react-i18next";
import React, { useState, Children } from 'react';
import { ArrowLeftIcon, PlusIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
interface AddRoomFormProps {
  onSave: (roomData: any) => void;
  onCancel: () => void;
}
export function AddRoomForm({
  onSave,
  onCancel
}: AddRoomFormProps) {
  const {
    t
  } = useTranslation("rooms");
  const [roomData, setRoomData] = useState({
    name: '',
    category: '',
    code: '',
    description: '',
    isSmoking: false,
    isAccessible: false,
    isActive: false,
    view: '',
    size: '',
    amenities: [] as string[],
    baseAdultsOccupancy: 2,
    baseChildrenOccupancy: 0,
    maxOccupancy: 2,
    allowAdditionalAdults: false,
    allowAdditionalChildren: false,
    bedConfigurations: [{
      type: '',
      count: 1
    }]
  });
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const amenityOptions = ['Wi-Fi', 'TV', 'Minibar', 'Air Conditioning', 'Balcony', 'Terrace', 'Ocean View', 'Private Pool', 'Jacuzzi', 'Slippers', 'Desk', 'Dining Area', 'Interconnecting Rooms', 'Non-Smoking', 'Beach Access', 'Breakfast Included'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const addBedConfig = () => {
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: [...prev.bedConfigurations, {
        type: '',
        count: 1
      }]
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-8848">
      <div className="flex items-center" data-id="element-8849">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-8850">
          
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-8851" />
          <span data-id="element-8852">{t("components.AddRoomForm.add_room")}</span>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8853">
        
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8854">
          <div data-id="element-8855">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8856">
              
              {t("components.AddRoomForm.room_name")}<span className="text-red-500" data-id="element-8857">*</span>
            </label>
            <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8858" />
            
          </div>
          <div data-id="element-8859">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8860">
              
              {t("components.AddRoomForm.room_category")}<span className="text-red-500" data-id="element-8861">*</span>
            </label>
            <div className="relative" data-id="element-8862">
              <select id="category" name="category" value={roomData.category} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-8863">
                
                <option value="" data-id="element-8864">{t("components.AddRoomForm.select_room_category")}</option>
                {roomCategories.map(category => <option key={category} value={category} data-id="element-8865">
                    {category.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-8866">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-8867">
                  
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-8868" />
                  
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-8869">
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8870">
            
            {t("components.AddRoomForm.room_code")}
          </label>
          <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} placeholder={t("components.AddRoomForm.attrs.enter_room_code")} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8871" />
          
        </div>
        <div className="mb-6" data-id="element-8872">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8873">
            
            {t("components.AddRoomForm.description")}
          </label>
          <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} placeholder={t("components.AddRoomForm.attrs.enter_room_description")} rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8874" />
          
        </div>
        <div className="flex flex-wrap gap-6 mb-6" data-id="element-8875">
          <div className="flex items-center" data-id="element-8876">
            <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8877" />
            
            <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-8878">
              {t("components.AddRoomForm.smoking")}
            </label>
          </div>
          <div className="flex items-center" data-id="element-8879">
            <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8880" />
            
            <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-8881">
              
              {t("components.AddRoomForm.accessibility")}
            </label>
          </div>
          <div className="flex items-center" data-id="element-8882">
            <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8883" />
            
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-8884">
              {t("components.AddRoomForm.status")}
            </label>
          </div>
        </div>
        {/* Room View and Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8885">
          <div data-id="element-8886">
            <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8887">
              
              {t("components.AddRoomForm.room_view")}<span className="text-red-500" data-id="element-8888">*</span>
            </label>
            <div className="relative" data-id="element-8889">
              <select id="view" name="view" value={roomData.view} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-8890">
                
                <option value="" data-id="element-8891">{t("components.AddRoomForm.select_room_view")}</option>
                {roomViews.map(view => <option key={view} value={view} data-id="element-8892">
                    {view.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-8893">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-8894">
                  
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-8895" />
                  
                </svg>
              </div>
            </div>
          </div>
          <div data-id="element-8896">
            <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8897">
              
              {t("components.AddRoomForm.room_size_sq_m")}
            </label>
            <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8898" />
            
          </div>
        </div>
        {/* Room Amenities */}
        <div className="mb-6" data-id="element-8899">
          <label htmlFor="amenities" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8900">
            
            {t("components.AddRoomForm.room_amenities")}
          </label>
          <select id="amenities" name="amenities" multiple value={roomData.amenities} onChange={e => {
          const options = Array.from(e.target.selectedOptions, option => option.value);
          setRoomData(prev => ({
            ...prev,
            amenities: options
          }));
        }} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8901">
            
            {amenityOptions.map(amenity => <option key={amenity} value={amenity} data-id="element-8902">
                {amenity}
              </option>)}
          </select>
        </div>
        {/* Occupancy Settings */}
        <div className="mb-6" data-id="element-8903">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-8904">
            {t("components.AddRoomForm.occupancy_settings")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-8905">
            <div data-id="element-8906">
              <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8907">
                
                {t("components.AddRoomForm.base_adults_occupancy")}<span className="text-red-500" data-id="element-8908">*</span>
              </label>
              <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8909" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-8910">
                {t("components.AddRoomForm.standard_number_of_allowed_adults_for")}
              </p>
            </div>
            <div data-id="element-8911">
              <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8912">
                
                {t("components.AddRoomForm.base_children_occupancy")}<span className="text-red-500" data-id="element-8913">*</span>
              </label>
              <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8914" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-8915">
                {t("components.AddRoomForm.standard_number_of_allowed_children_for")}
              </p>
            </div>
            <div data-id="element-8916">
              <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8917">
                
                {t("components.AddRoomForm.maximum_occupancy")}<span className="text-red-500" data-id="element-8918">*</span>
              </label>
              <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8919" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-8920">
                {t("components.AddRoomForm.maximum_number_of_guests_allowed")}
              </p>
            </div>
          </div>
          <div className="space-y-2 mb-4" data-id="element-8921">
            <div className="flex items-center" data-id="element-8922">
              <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8923" />
              
              <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-8924">
                
                {t("components.AddRoomForm.allow_additional_adults")}
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-8925">
              {t("components.AddRoomForm.allow_additional_adults_not_exceeding_total")}
            </p>
            <div className="flex items-center mt-2" data-id="element-8926">
              <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8927" />
              
              <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-8928">
                
                {t("components.AddRoomForm.allow_additional_children")}
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-8929">
              {t("components.AddRoomForm.allow_additional_children_not_exceeding_total")}
            </p>
          </div>
          {/* Occupancy Summary */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-8930">
            <div className="flex" data-id="element-8931">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-8932" />
              <div data-id="element-8933">
                <p className="text-sm text-red-700" data-id="element-8934">
                  {t("components.AddRoomForm.this_room_has_a_base_occupancy")}{' '}
                  {roomData.baseAdultsOccupancy} {t("components.AddRoomForm.adults_and")}{' '}
                  {roomData.baseChildrenOccupancy} {t("components.AddRoomForm.children_with_a_total_maximum_occupancy")} {roomData.maxOccupancy}.
                </p>
                <p className="text-sm text-red-700" data-id="element-8935">
                  {t("components.AddRoomForm.additional_adults_are_not_allowed")}
                </p>
                <p className="text-sm text-red-700" data-id="element-8936">
                  {t("components.AddRoomForm.additional_children_are_not_allowed")}
                </p>
                <p className="text-sm text-red-700" data-id="element-8937">
                  {t("components.AddRoomForm.additional_cost_per_child_or_adults")}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Bed Configuration */}
        <div className="mb-6" data-id="element-8938">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-8939">
            {t("components.AddRoomForm.bed_configuration")}<span className="text-red-500" data-id="element-8940">*</span>
          </h3>
          {roomData.bedConfigurations.map((config, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-8941">
            
              <div data-id="element-8942">
                <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8943">
                
                  {t("components.AddRoomForm.bed_type")}
                </label>
                <div className="relative" data-id="element-8944">
                  <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-8945">
                  
                    <option value="" data-id="element-8946">{t("components.AddRoomForm.select_bed_type")}</option>
                    {bedTypes.map(type => <option key={type} value={type} data-id="element-8947">
                        {type}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-8948">
                    <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-8949">
                    
                      <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-8950" />
                    
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center" data-id="element-8951">
                <div className="flex-grow" data-id="element-8952">
                  <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8953">
                  
                    {t("components.AddRoomForm.bed_count")}
                  </label>
                  <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8954" />
                
                </div>
                {index > 0 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-8955">
                
                    <TrashIcon size={18} data-id="element-8956" />
                  </button>}
              </div>
            </div>)}
          <button type="button" onClick={addBedConfig} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-8957">
            
            <PlusIcon size={16} className="mr-1" data-id="element-8958" />
            {t("components.AddRoomForm.add_bed_configuration")}
          </button>
        </div>
        {/* Submit Button */}
        <div className="flex justify-end" data-id="element-8959">
          <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-8960">
            
            {t("components.AddRoomForm.save")}
          </button>
        </div>
      </form>
    </div>;
}